// Web Storage (localStorage / sessionStorage) access that degrades instead of
// throwing. Browsers throw on storage access when site data is blocked, in some
// private modes, and when the quota is exhausted — always as a DOMException
// (SecurityError, QuotaExceededError, …). Those are the only failures handled
// here: a read degrades to "nothing stored" and a write or remove reports that
// it did not take effect, so each caller decides explicitly what an unpersisted
// value means for it. Any other error is a bug and propagates.

export type StorageArea = 'local' | 'session';

function storageFor(area: StorageArea): Storage {
  // The getter itself throws (SecurityError) when site data is blocked.
  return area === 'local' ? window.localStorage : window.sessionStorage;
}

export function readStorage(area: StorageArea, key: string): string | null {
  try {
    return storageFor(area).getItem(key);
  } catch (err) {
    if (err instanceof DOMException) return null;
    throw err;
  }
}

/** Returns whether the value was persisted. */
export function writeStorage(area: StorageArea, key: string, value: string): boolean {
  try {
    storageFor(area).setItem(key, value);
    return true;
  } catch (err) {
    if (err instanceof DOMException) return false;
    throw err;
  }
}

/** Returns whether the key is now absent from storage. */
export function removeStorage(area: StorageArea, key: string): boolean {
  try {
    storageFor(area).removeItem(key);
    return true;
  } catch (err) {
    if (err instanceof DOMException) return false;
    throw err;
  }
}
